import { showDanger } from 'containers/Toaster/actions';
import { makeSelectedSnapshot } from 'pages/SnapshotManagement/selectors';
import { PureComponent } from 'react';
import { createStructuredSelector } from 'reselect';
import { connectAsAny } from 'utils/redux/connectAsAny';

import Bluebird from 'bluebird';
import { Button } from 'components/Button/Button';
import { convertGraphToImage } from 'containers/DownloadSiteReport/helpers/convertGraphToImage';
import { groupDataByType } from 'containers/DownloadSiteReport/helpers/groupDataByType';
import withLogError from 'containers/ErrorBoundary/withLogError';
import DocxGen from 'docxtemplater';
import FileSaver from 'file-saver';
import { pipe } from 'fp-ts/lib/function';
import imageSize from 'image-size';
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils';
import PropTypes from 'prop-types';
import handleEvent from 'utils/handleEvent';
import { withAppProps } from 'utils/react/withAppProps';
import { fetchSiteReportData } from './actions';

const loadFile = (url, callback) => {
  PizZipUtils.getBinaryContent(url, (err, data) => callback(err, data));
};

const chartDataToImage = async ({ chartData, ...rest }) =>
  chartData
    ? {
        ...rest,
        chartImage: await convertGraphToImage(chartData),
      }
    : rest;

@withLogError
@withAppProps
class BareDownloadSiteReport extends PureComponent {
  static propTypes = {
    accent: PropTypes.string,
    attributeFilters: PropTypes.object.isRequired,
    buttonTitle: PropTypes.string,
    catchError: PropTypes.func.isRequired,
    hideText: PropTypes.bool,
    logError: PropTypes.func.isRequired,
    // makes Tooltip applicable
    onMouseEnter: PropTypes.func,
    onMouseLeave: PropTypes.func,
    siteName: PropTypes.string.isRequired,
    size: PropTypes.string,
    snapshot: PropTypes.string.isRequired,
    templateFile: PropTypes.string,
    variant: PropTypes.string.isRequired,
    dateFormat: PropTypes.string.isRequired,
    dateTimeFormat: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
  };

  static defaultProps = {
    templateFile: '/public/reports/site.docx',
  };

  constructor(props) {
    super(props);
    // width determined experimentally to match document content width
    this.chartSize = { width: 570, height: 600 };
    this.state = { loading: false };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getImageSize = () => (img) => {
    const size = imageSize(img);
    if (
      size.width <= this.chartSize.width &&
      size.height <= this.chartSize.height
    ) {
      return [size.width, size.height];
    }
    const wBh = size.width > size.height;
    const ratio = wBh ? size.height / size.width : size.width / size.height;
    let startSize = wBh ? this.chartSize.width : this.chartSize.height;
    while (
      startSize * ratio >
      (wBh ? this.chartSize.height - 80 : this.chartSize.width - 80)
    ) {
      // -80 kvuli 1 radku s textem nazev obrazku
      startSize -= 10;
    }
    return wBh
      ? [startSize, startSize * ratio]
      : [startSize * ratio, startSize];
  };

  hadnleDownloadBtnClick = handleEvent(() => {
    const {
      attributeFilters,
      snapshot,
      templateFile,
      dispatch,
      siteName,
      catchError,
      logError,
      dateFormat,
      dateTimeFormat,
      timezone,
    } = this.props;
    if (this._isMounted) {
      this.setState(
        { loading: true },
        loadFile(templateFile, (err, content) => {
          if (err) {
            return this.downlodFailed(err);
          }
          return catchError(() =>
            dispatch(
              fetchSiteReportData({
                attributeFilters,
                siteName,
                snapshot,
                dateFormat,
                dateTimeFormat,
                timezone,
                resolve: async (response) => {
                  try {
                    const data = await this.processSiteReportData(response);
                    const ImageModule = require('docxtemplater-image-module');
                    const opts = {
                      centered: false,
                      getImage: (tagValue) => tagValue,
                      getSize: this.getImageSize(),
                    };
                    const zip = new PizZip(content);
                    const doc = new DocxGen()
                      .loadZip(zip)
                      .setOptions({ nullGetter: () => '' });
                    doc.attachModule(new ImageModule(opts));
                    doc.setData(data);
                    doc.render();
                    const out = doc.getZip().generate({ type: 'blob' });
                    this.downloadFinished();
                    // !! DO NOT REUSE
                    // Downloads should be handled by providing proper headers from backend and initiated using `<a href=""`
                    // to use browser's native progress indication and avoid issues with large files.
                    // In this case we are building a word document in the browser, which is not a perfect solution at all.
                    FileSaver.saveAs(out, `site_${data.siteName}_report.docx`); // !! DO NOT REUSE
                  } catch (e) {
                    this.downlodFailed(`${e}`);
                    logError(e);
                  }
                },
                reject: () => {
                  this.downloadFinished();
                },
              }),
            ),
          );
        }),
      );
    }
  });

  downloadFinished() {
    if (this._isMounted) {
      this.setState({
        loading: false,
      });
    }
  }

  downlodFailed(err) {
    this.downloadFinished();
    return this.props.onShowDanger({ message: err });
  }

  async processSiteReportData({ data, endTime, siteName }) {
    return {
      ...pipe(await Bluebird.all(data.map(chartDataToImage)), groupDataByType),
      endTime,
      siteName,
    };
  }

  render() {
    const { variant, accent, size } = this.props;
    const { loading } = this.state;
    return (
      <Button
        type='button'
        variant={variant}
        accent={accent}
        size={size}
        onClick={this.hadnleDownloadBtnClick}
        icon='fa-download'
        loading={loading}
        onMouseEnter={this.props.onMouseEnter}
        onMouseLeave={this.props.onMouseLeave}
      >
        {!this.props.hideText && this.props.buttonTitle}
      </Button>
    );
  }
}

export const DownloadSiteReport = connectAsAny(
  createStructuredSelector({
    snapshot: makeSelectedSnapshot(),
  }),
  (dispatch) => ({
    onShowDanger: (args) => dispatch(showDanger(args)),
    dispatch,
  }),
)(BareDownloadSiteReport);
